/**
 * The side rail down the left edge of every admin screen.
 *
 * Built from the handoff's runnable prototype — `shared/side-nav.jsx` and the
 * `.sn-*` rules in `shared/modals.css` — rather than §6's prose, which gives
 * only "56px, icon-only". That prose describes the collapsed state: the
 * prototype renders 240px with labels and an identity block, and collapses to
 * 64px icon-only under 1024px. Its own `03-catalogue/README.md` confirms the
 * reference screenshots were "captured at narrow preview width", which is why
 * all nineteen of them show the collapsed form.
 *
 * Collapsed, the labels are gone and every item needs its name some other way:
 * `aria-label` for assistive tech, `Tooltip` for a sighted user. Neither
 * substitutes for the other — a tooltip is not announced, an aria-label is not
 * seen. Expanded, the visible label is the accessible name and the tooltip
 * would only repeat it, so both are dropped.
 */
import type { ComponentType } from 'react';
import { Link } from '@tanstack/react-router';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { AdminLogoutButton } from '../ui';

/** The routes the rail can point at — kept as a union so `Link` stays type-checked. */
export type NavRoute =
  | '/contests'
  | '/operations'
  | '/catalogue'
  | '/users'
  | '/assets'
  | '/prize-tier-defaults'
  | '/sports'
  | '/configuration'
  | '/qa-seed';

export interface NavItem {
  to: NavRoute;
  label: string;
  Icon: ComponentType<SvgIconProps>;
}

interface AdminNavRailProps {
  items: readonly NavItem[];
  /** The active item is the one whose `to` prefixes the current path. */
  pathname: string;
  displayName: string;
  expanded: boolean;
  /** False below the design's breakpoint, where collapsed is not negotiable. */
  canToggle: boolean;
  onToggle: () => void;
}

/** Wraps in a Tooltip only when the label is not already on screen. */
const WithNameWhenCollapsed = ({
  expanded,
  label,
  children,
}: {
  expanded: boolean;
  label: string;
  children: React.ReactElement;
}) =>
  expanded ? (
    children
  ) : (
    <Tooltip title={label} placement="right">
      {children}
    </Tooltip>
  );

export const AdminNavRail = ({
  items,
  pathname,
  displayName,
  expanded,
  canToggle,
  onToggle,
}: AdminNavRailProps) => (
  <Box
    component="aside"
    sx={(theme) => {
      const { navRail } = theme.tokens;
      return {
        position: 'sticky',
        top: 0,
        height: '100vh',
        flexShrink: 0,
        width: expanded ? navRail.widthExpanded : navRail.widthCollapsed,
        padding: expanded
          ? `${navRail.railPaddingY}px ${navRail.railPaddingX}px`
          : `${navRail.railPaddingYCollapsed}px ${navRail.railPaddingXCollapsed}px`,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.tokens.color.surface,
        borderRight: theme.tokens.border.default,
        transition: 'width .16s ease, padding .16s ease',
      };
    }}
  >
    <Box
      sx={(theme) => {
        const { navRail } = theme.tokens;
        return {
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'flex-start' : 'center',
          gap: `${navRail.itemGap}px`,
          padding: expanded ? navRail.identityPadding : navRail.identityPaddingCollapsed,
          marginBottom: `${theme.tokens.space[4] - 2}px`,
          borderRadius: `${navRail.identityRadius}px`,
          bgcolor: theme.tokens.color.surfaceMuted,
          border: theme.tokens.border.default,
        };
      }}
    >
      {/*
          The tooltip goes on the Avatar, not the row: the Avatar is the one
          element here with a role that ARIA permits naming, so wrapping the
          bare row instead would have MUI inject an aria-label onto a generic
          div and leave two competing names for the same thing.
        */}
      <WithNameWhenCollapsed expanded={expanded} label={displayName}>
        <Avatar
          // MUI renders Avatar as a bare div, and ARIA prohibits naming a
          // role=generic element. Collapsed, this is the only identity on
          // screen, so it has to carry a real role to be announced at all.
          role="img"
          aria-label={`Signed in as ${displayName}`}
          sx={(theme) => {
            const { navRail } = theme.tokens;
            return {
              width: navRail.avatarSize,
              height: navRail.avatarSize,
              flexShrink: 0,
              fontSize: navRail.nameSize,
              fontWeight: 700,
              // The prototype's `.sn-avatar` gradient, in tokens: ink → ink-2.
              backgroundImage: `linear-gradient(135deg, ${theme.tokens.color.ink}, ${theme.tokens.color.ink2})`,
              color: theme.tokens.color.surface,
            };
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
      </WithNameWhenCollapsed>
      {expanded ? (
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            sx={(theme) => ({
              fontSize: theme.tokens.navRail.nameSize,
              fontWeight: theme.tokens.navRail.activeWeight,
              letterSpacing: theme.tokens.navRail.labelTracking,
              color: theme.tokens.color.ink,
            })}
          >
            {displayName}
          </Typography>
          <Typography
            noWrap
            sx={(theme) => ({
              fontSize: theme.tokens.navRail.roleSize,
              color: theme.tokens.color.ink4,
            })}
          >
            Administrator
          </Typography>
        </Box>
      ) : null}
    </Box>

    <Box
      component="nav"
      aria-label="Admin navigation"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        // The rail is a sticky 100vh column, so anything past the fold is
        // unreachable at every scroll position. On a short viewport the
        // destinations must scroll rather than squash, so that the identity
        // and logout below them stay on screen and keep their hit targets.
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      {items.map(({ to, label, Icon }) => {
        const isActive = pathname.startsWith(to);
        return (
          <WithNameWhenCollapsed key={to} expanded={expanded} label={label}>
            <Box
              component={Link}
              to={to}
              aria-label={expanded ? undefined : label}
              aria-current={isActive ? 'page' : undefined}
              sx={(theme) => {
                const { navRail } = theme.tokens;
                return {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: expanded ? 'flex-start' : 'center',
                  gap: `${navRail.itemGap}px`,
                  padding: navRail.itemPadding,
                  flexShrink: 0,
                  borderRadius: `${theme.tokens.radius.pill}px`,
                  fontSize: navRail.labelSize,
                  fontWeight: isActive ? navRail.activeWeight : navRail.labelWeight,
                  letterSpacing: navRail.labelTracking,
                  textDecoration: 'none',
                  color: isActive ? theme.tokens.color.surface : theme.tokens.color.ink2,
                  bgcolor: isActive ? theme.tokens.color.accent : 'transparent',
                  transition: 'background-color .12s, color .12s',
                  '&:hover': {
                    bgcolor: isActive ? theme.tokens.color.accent : theme.tokens.color.bg,
                    color: isActive ? theme.tokens.color.surface : theme.tokens.color.ink,
                  },
                };
              }}
            >
              <Box
                component="span"
                sx={(theme) => ({
                  display: 'inline-grid',
                  placeItems: 'center',
                  width: theme.tokens.navRail.iconSize,
                  height: theme.tokens.navRail.iconSize,
                  flexShrink: 0,
                  opacity: isActive ? 1 : 0.85,
                })}
              >
                <Icon sx={{ fontSize: (theme) => theme.tokens.navRail.iconSize }} />
              </Box>
              {expanded ? (
                <Box
                  component="span"
                  sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {label}
                </Box>
              ) : null}
            </Box>
          </WithNameWhenCollapsed>
        );
      })}
    </Box>

    <Box
      sx={(theme) => ({
        flexShrink: 0,
        marginTop: `${theme.tokens.space[3] - 2}px`,
        paddingTop: `${theme.tokens.space[4] - 2}px`,
        borderTop: theme.tokens.border.default,
        display: 'flex',
        justifyContent: expanded ? 'flex-start' : 'center',
      })}
    >
      <AdminLogoutButton expanded={expanded} />
    </Box>

    {/*
      The handoff has no collapse control — its rail is purely responsive — so
      this is ours. It sits on the border rather than inside the column so it
      never competes with a destination for width, and it is absent below the
      breakpoint where collapsed is not a choice.
    */}
    {canToggle ? (
      <Tooltip title={expanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
        <IconButton
          onClick={onToggle}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={expanded}
          size="small"
          sx={(theme) => ({
            position: 'absolute',
            top: '50%',
            // `right: 0` plus the half-width shift lands the control centred on
            // the rail's border. It reads as offset-by-padding but is not: the
            // translate makes up exactly the padding it is inset by.
            right: 0,
            transform: 'translate(50%, -50%)',
            width: 22,
            height: 22,
            zIndex: 1,
            bgcolor: theme.tokens.color.surface,
            border: theme.tokens.border.default,
            color: theme.tokens.color.ink3,
            boxShadow: theme.tokens.shadow.card,
            '&:hover': {
              bgcolor: theme.tokens.color.bg,
              color: theme.tokens.color.ink,
            },
          })}
        >
          {expanded ? (
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    ) : null}
  </Box>
);
